import { useEffect, useRef } from 'react'
import { useRouter } from 'next/router'
import log from 'loglevel'
import { scrollUserPosition, previousRoute, sessionStorageItem } from 'hooks/common/useSessionStorage'

// SessionStorageキー
export const sessionStorageKey = {
  localRecipe: 'localRecipe',
  releaseRecipe: 'releaseRecipe',
  lastRecipeBookId: 'lastRecipeBookId',
  localRecipeIndex: 'localRecipeIndex',
  releaseRecipeIndex: 'releaseRecipeIndex',
  releaseRecipeInfo: 'releaseRecipeInfo',
}

/**
 * 共通 URLがフルパスの場合、パス部だけ抽出する 
 * @param {string} url チェック対象URL
 */
const urlFormat = (url) => {
  // 不正なURLなら空白を返す
  if (!url) {
    return '';
  }

  // フルパスが入ることがあるのでパス部だけ抽出
  if (url.startsWith('http://') || url.startsWith('https://')) {
    url = url.replace(/^https?:\/\/[^\/]+/i, '');
  }

  return url;
}


// ==========================================================
// 保存レシピ 離脱時用処理
// ==========================================================
export const useScrollResetForLocalRecipe = () => {
  /** const **/
  const router = useRouter()

  // スクロール位置有効ページ
  const localRecipePageList = [
    '/recipe/detail',
    '/recipe/detail/other',
    '/recipe/index',
  ]

  /**
   * 保存レシピの表示位置を取得
   * @return {Number} 表示位置
   */
  const getLocalRecipeIndexForStorage = () => {
    log.debug("[useScrollSave]getLocalRecipeIndexForStorage start")
    return Number(sessionStorageItem(sessionStorageKey.localRecipeIndex).getSessionStorageItem()) || 0;
  }

  /**
   * 保存レシピの表示位置をセット
   * @param {Number} index 表示位置
   */
  const setLocalRecipeIndexForStorage = (index) => {
    log.debug("[useScrollSave]setLocalRecipeIndexForStorage start")
    sessionStorageItem(sessionStorageKey.localRecipeIndex).setSessionStorageItem(index);
  }

  /**
   * 保存レシピの表示位置を削除
   */
  const removeLocalRecipeIndexForStorage = () => {
    log.debug("[useScrollSave]removeLocalRecipeIndexForStorage start")
    sessionStorageItem(sessionStorageKey.localRecipeIndex).removeSessionStorageItem();
  }

  /**
   * スクロール位置リセット 
   * @param {string} url チェック対象URL
   */
  const removeLocalRecipeIndex = (url) => {
    log.debug("[useScrollSave]removeLocalRecipeIndex start")

    // スクロール位置リセットフラグ
    let isReset = true;

    // スクロール位置情報リセット判定
      localRecipePageList.forEach(item => {
      if (url && url.indexOf(item) === 0) {
          isReset = false;
        }
      });

    if (isReset) {
      sessionStorageItem(sessionStorageKey.localRecipeIndex).removeSessionStorageItem();
    }
  }

  // スクロール記録用useEffect
  useEffect(() => {
    // ページ離脱時のイベント処理
    const handleRouteChangeStartinLocalRecipe = (url) => {
      try {
        log.debug("[useScrollSave]handleRouteChangeStartinLocalRecipe remove local recipe url: ", url)
        
        // フルパスが入ることがあるのでパス部だけ抽出
        let checkUrl = urlFormat(url);

        // スクロール位置リセット
        removeLocalRecipeIndex(checkUrl);

      } catch (e) {
        // 例外出力して続行
        log.error("[useScrollSave]handleRouteChangeStartinLocalRecipe remove local recipe error:", e);
      }
    }

    // リロード対策
    const beforeUnloadHandlerinLocalRecipe = (e) => {
      log.debug("[useScrollSave]beforeUnloadHandlerinLocalRecipe start")

      // 離脱時(リロード等)は現在画面のパスを保存
      previousRoute().setPreviousRoute(location.pathname);
    }

    try {
      // 初期化
      removeLocalRecipeIndex(previousRoute().getPreviousRoute());

      // イベントセット
      router.events.on('routeChangeStart', handleRouteChangeStartinLocalRecipe)
      window.addEventListener('beforeunload', beforeUnloadHandlerinLocalRecipe);
    } catch (e) {
      log.error("[useScrollSave]useEffect set local recipe event error:", e);
    }

    return () => {
      // イベント解除
      router.events.off('routeChangeStart', handleRouteChangeStartinLocalRecipe)
      window.removeEventListener("beforeunload", beforeUnloadHandlerinLocalRecipe);
    }
  }, [router.asPath])

  return {
    getLocalRecipeIndexForStorage,
    setLocalRecipeIndexForStorage,
    removeLocalRecipeIndexForStorage,
    localRecipePageList,
  }
}


// ==========================================================
// 詳細ページ離脱時用処理
// ==========================================================
export const useScrollResetForReleaseRecipeDetail = () => {
  /** const **/
  const router = useRouter()

  // スクロール位置有効ページ
  const releaseRecipePageList = [
    '/recipe/release/',
    '/recipe/release/detail/',
  ]

  // スクロール記録用useEffect
  useEffect(() => {
    // ページ離脱時のイベント処理
    const handleRouteChangeStartinRecipeindex = (url) => {
      try {
        log.debug("[useScrollSave]handleRouteChangeStart remove detail url: ", url)

        // フルパスが入ることがあるのでパス部だけ抽出
        let checkUrl = urlFormat(url);

        // スクロール位置リセットフラグ
        let isReset = true;

        releaseRecipePageList.forEach(item => {
          if (checkUrl && checkUrl.indexOf(item) === 0) {
            isReset = false;
          }
        });

        if (isReset) {
          scrollUserPosition().removeUserScrollPositionY(sessionStorageKey.releaseRecipe);
          sessionStorageItem(sessionStorageKey.lastRecipeBookId).removeSessionStorageItem();
          sessionStorageItem(sessionStorageKey.releaseRecipeIndex).removeSessionStorageItem();
          sessionStorageItem(sessionStorageKey.releaseRecipeInfo).removeSessionStorageItem();
        }

      } catch (e) {
        // 例外出力して続行
        log.error("[useScrollSave]handleRouteChangeStart remove detail error:", e);
      }
    }

    try {
      // イベントセット
      router.events.on('routeChangeStart', handleRouteChangeStartinRecipeindex)
    } catch (e) {
      log.error("[useScrollSave]useEffect set detail page event error:", e);
    }

    return () => {
      // イベント解除
      router.events.off('routeChangeStart', handleRouteChangeStartinRecipeindex)
    }
  }, [router.asPath])
}


// ==========================================================
// スクロール位置保存フック(公開中レシピ用)
// ==========================================================
export const useScrollSaveForReleaseRecipe = () => {
  /** const **/
  const router = useRouter()
  const isTransitioning = useRef(true) // ページ遷移中フラグ（初期値true：ページロード時は保存しない）

  // スクロール位置が有効な画面リスト
  const releaseRecipePageList = [
    '/recipe/release/',
    '/recipe/release/detail/',
  ]

  /**
   * 読み込み件数取得
   * @return {Number} 読み込みした件数
   */
  const getRecipeIndex = () => {
    log.debug("[useScrollSave]getRecipeindex start")
    return Number(sessionStorageItem(sessionStorageKey.releaseRecipeIndex).getSessionStorageItem()) || 0;
  }

  /**
   * 読み込み件数セット
   * @param {Number} index 読み込みした件数
   */
  const setRecipeIndex = (index) => {
    log.debug("[useScrollSave]setRecipeindex start")
    sessionStorageItem(sessionStorageKey.releaseRecipeIndex).setSessionStorageItem(index);
  }

  /**
   * レシピの高さ情報を取得
   * @return {JSON} 読み込みした件数
   */
  const getRecipeInfo = () => {
    log.debug("[useScrollSave]getRecipeInfo start")
    const info = sessionStorageItem(sessionStorageKey.releaseRecipeInfo).getSessionStorageItem() || null;
    const resInfo = []

    // 利用時は配列型に整形
    if (info) {
      // JSON型文字列を返還
      const parsedInfo = JSON.parse(info);

      // IDをキーにして配列化
      Object.keys(parsedInfo).forEach((key) => {
        resInfo[parsedInfo[key]["id"]] = {
          height: Number(parsedInfo[key]["height"])
        }
      })
    }

    return resInfo;
  }

  /**
   * レシピの高さ情報を取得
   * @return {JSON} 読み込みした件数
   */
  const setRecipeInfo = () => {
    log.debug("[useScrollSave]setRecipeInfo start")

    // スクロール時に表示中のレシピの高さ情報を取得
    const item = document.getElementsByClassName('release-recipe-item');

    // 保存時はJSON型を文字列化して保存
    const itemInfo = []
    for( let i = 0; i < item.length; i++ ) {
      itemInfo.push({
        id: item[i].dataset.id,
        height: item[i].height
      })
    }

    sessionStorageItem(sessionStorageKey.releaseRecipeInfo).setSessionStorageItem(JSON.stringify(itemInfo));
  }

  /**
   * 最後に表示したブックID取得
   * @return {String} 最後に表示したブックID
   */
  const getLastRecipeBookId = () => {
    log.debug("[useScrollSave]getLastRecipeBookId start")
    return sessionStorageItem(sessionStorageKey.lastRecipeBookId).getSessionStorageItem() || '';
  }

  /**
   * 公開レシピ位置情報セット
   * @param {Number} userPosition スクロール位置
   */
  const setReleaseRecipePosition = (userPosition) => {
    log.debug("[useScrollSave]setReleaseRecipePosition start")
    sessionStorageItem(sessionStorageKey.releaseRecipe).setSessionStorageItem(userPosition);
  }

  /**
   * スクロール位置セット 
   * @param {Number} userPosition スクロール位置
   */
  const setScrollPosition = (userPosition) => {
    log.debug("[useScrollSave]setScrollPosition start")

    // パラメータをからbookIdを取り出す
    const params = new URLSearchParams(window.location.search);
    const bookId = params.get('bookId');

    // SessionStorageにスクロール位置情報を保存
    scrollUserPosition().setUserScrollPositionY(sessionStorageKey.releaseRecipe, userPosition);
    sessionStorageItem(sessionStorageKey.lastRecipeBookId).setSessionStorageItem(bookId); // 最後に見た公開レシピのブックIDを保存
  }

  /**
   * ページ表示時にスクロール位置を移動
   * @param {string} key 保存先SessionStorageキー
   */
  const moveScrollPosition = (key) => {
    log.debug("[useScrollSave]moveScrollPosition start")

    // パラメータを取り出す
    const params = new URLSearchParams(window.location.search);
    const bookId = params.get('bookId');
    const lastBookId = sessionStorageItem(sessionStorageKey.lastRecipeBookId).getSessionStorageItem();

    // 一致していなければ保持情報を削除
    if (lastBookId && bookId && lastBookId !== bookId) {
      log.debug("[useScrollSave]moveScrollPosition bookId mismatch skip");
      sessionStorageItem(sessionStorageKey.lastRecipeBookId).removeSessionStorageItem();
      scrollUserPosition().removeUserScrollPositionY(key);
      sessionStorageItem(sessionStorageKey.releaseRecipeIndex).removeSessionStorageItem();
      sessionStorageItem(sessionStorageKey.releaseRecipeInfo).removeSessionStorageItem();
      return {}
    }

    // スクロール位置取得
    const userPosition = scrollUserPosition().getUserScrollPositionY(key);

    // 座標移動
    if (userPosition && userPosition > 0) {
      log.debug("[useScrollSave]moveScrollPosition move")

      // 遅延実行
      const attempts = [300];
      attempts.forEach(delay => {
        log.debug("[useScrollSave]moveScrollPosition setTimeout delay:", delay)
        setTimeout(() => {
          window.scrollTo({
            top: userPosition,
            // behavior: 'smooth',
            behavior: 'auto',
          });
        }, delay);
      });
    }

    return {}
  }

  /**
   * スクロール位置リセット
   * @param {string} url チェック対象URL
   */
  const removeScrollPosition = (url) => {
    log.debug("[useScrollSave]removeScrollPosition start")

    // スクロール位置リセットフラグ
    let isReset = true;

    // スクロール位置情報リセット判定
      releaseRecipePageList.forEach(item => {
      if (url && url.indexOf(item) === 0) {
          isReset = false;
        }
      });

    // パラメータを取り出す
    const params = new URLSearchParams(window.location.search);
    const bookId = params.get('bookId');
    const lastBookId = sessionStorageItem(sessionStorageKey.lastRecipeBookId).getSessionStorageItem();

    // bookIdが変更されていたらリセット対象とする
    if (bookId && lastBookId && bookId !== lastBookId) {
      isReset = true;
    }

    if (isReset) {
      sessionStorageItem(sessionStorageKey.lastRecipeBookId).removeSessionStorageItem();
      scrollUserPosition().removeUserScrollPositionY(sessionStorageKey.releaseRecipe);
      sessionStorageItem(sessionStorageKey.releaseRecipeIndex).removeSessionStorageItem();
      sessionStorageItem(sessionStorageKey.releaseRecipeInfo).removeSessionStorageItem();
    }
  }

  // ルート変更時用useEffect
  useEffect(() => {
    log.debug("[useScrollSave]handleRouteChange useEffect start")
    isTransitioning.current = true; // 遷移処理中
    let inThrottle = false; // スロットル制御
    const delayTime = 200; // 遅延実行 ミリ秒

    const handleScroll = () => {
      // 遷移中は保存しない
      if (isTransitioning.current) {
        return;
      }

      // 一定間隔で実行
      if (!inThrottle) {
        // スロットル開始
        inThrottle = true;

        // タイマー内で処理実行
        setTimeout(() => {
          const userPosition = window.scrollY;

          // 移動が完了した時点で保存
          window.requestAnimationFrame(() => {
            // 現在のスクロール位置を保存
            setScrollPosition(userPosition);
          })

          // レシピの高さ情報を保存
          setRecipeInfo();

          // スロットル解除
          inThrottle = false;
        }, delayTime);
      }
    };

    /**
     * ページ離脱時のイベント処理
     * @param {string} url 移動先URL
     */
    const handleRouteChangeStartinScrollSave = (url) => {
      try {
        log.debug("[useScrollSave]handleRouteChangeStartinScrollSave url", url)
        removeScrollPosition(url);
        window.removeEventListener("scroll", handleScroll); // 明示的削除
      } catch (e) {
        // 例外出力して続行
        log.error("[useScrollSave]handleRouteChangeStartinScrollSave error:", e);
      }
    }

    /**
     * リロード対策
     * @param {Object} event イベント情報
     */
    const beforeUnloadHandlerinScrollSave = (event) => {
      log.debug("[useScrollSave]beforeUnloadHandlerinScrollSave start")

      // 離脱時(リロード等)は現在画面のパスを保存
      previousRoute().setPreviousRoute(location.pathname);
    }

    try {
      // 前のページがレシピ詳細画面でなければスクロール位置を削除
      removeScrollPosition(previousRoute().getPreviousRoute());

      // イベントセット
      router.events.on('routeChangeStart', handleRouteChangeStartinScrollSave)
      window.addEventListener("scroll", handleScroll);
      window.addEventListener('beforeunload', beforeUnloadHandlerinScrollSave);
    } catch (e) {
      log.error("[useScrollSave]useEffect set scroll save event error:", e);
    }

    return () => {
      // イベント解除
      router.events.off('routeChangeStart', handleRouteChangeStartinScrollSave)
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("beforeunload", beforeUnloadHandlerinScrollSave);
    }
  }, [
    router.asPath,
    router.query,
  ]);

  return {
    /**
     * ページ遷移状態セット関数
     * @param {boolean} value 遷移中フラグ
     */
    setTransitioning: (value) => {
      isTransitioning.current = value
    },
    moveScrollPosition,
    getRecipeIndex,
    setRecipeIndex,
    getRecipeInfo,
    getLastRecipeBookId,
    setReleaseRecipePosition,
    releaseRecipePageList,
  }
}
